import { useMemo } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { CheckCircleIcon } from "@heroicons/react/24/solid";

import { IKanbanVehicle, VehiclePipelineStage } from "../../models/Vehicle";
import useVehicleKanban from "../../data/useVehicleKanban";
import useVehicleStats from "../../data/useVehicleStats";
import openInNewTab from "../../utils/openInNewTab";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import usePricingAccess from "../../hooks/usePricingAccess";
import LoadingWheel from "../shared/LoadingWheel";

// Columns shown on the board, left to right, between "Not On Site" and "Completed".
const KANBAN_COLUMN_STAGES: { stage: VehiclePipelineStage; labelKey: string }[] = [
  { stage: VehiclePipelineStage.NotOnSite, labelKey: "Purchased" },
  { stage: VehiclePipelineStage.PreTriage, labelKey: "Pre-Triage" },
  { stage: VehiclePipelineStage.Stored, labelKey: "Stored" },
  { stage: VehiclePipelineStage.AwaitingDismantling, labelKey: "Awaiting Dismantling" },
  { stage: VehiclePipelineStage.Dismantling, labelKey: "Dismantling" },
  { stage: VehiclePipelineStage.Dismantled, labelKey: "Dismantled" },
];

// Whole-number days since the vehicle was created, for an at-a-glance age badge.
function ageInDays(created: string): number | null {
  if (!created) return null;
  const days = DateTime.now().diff(DateTime.fromISO(created, { zone: "utc" }), "days").days;
  return Math.max(0, Math.floor(days));
}

function KanbanCard({ item }: { item: IKanbanVehicle }) {
  const { t } = useTranslation("vehicles");
  const { canViewPrices } = usePricingAccess();
  const { vehicle, todos } = item;
  const age = ageInDays(vehicle.created);
  const lead = vehicle.leads?.[0];
  const price = canViewPrices ? lead?.price : undefined;

  return (
    <div
      onClick={() => openInNewTab(`vehicles/${vehicle.id}`)}
      className="bg-white rounded-lg border border-gray-300 p-3 shadow-sm hover:shadow-md hover:border-primary-400 cursor-pointer transition"
    >
      <div className="flex gap-x-2">
        <div className="flex-shrink-0 flex items-center justify-center w-16 h-12">
          {vehicle.vehicleImageUrl ? (
            <img className="w-16 h-12 object-contain" src={vehicle.vehicleImageUrl} alt={vehicle.vrm} />
          ) : (
            <CameraIcon className="w-8 h-8 text-gray-400" />
          )}
        </div>

        <div className="flex flex-col min-w-0 flex-grow">
          <span className="font-semibold text-sm truncate">{vehicle.vrm}</span>
          <span className="text-xs text-gray-600 truncate">
            {[vehicle.combinedMake, vehicle.modelRangeDescription].filter(Boolean).join(" ") || "-"}
          </span>
          {price ? <span className="text-xs text-gray-800">{PriceFormat(price)}</span> : null}
        </div>
      </div>

      {todos?.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-400">{t("To do")}</span>
          <ul className="mt-1 flex flex-col gap-1">
            {todos.map((todo, i) => (
              <li
                key={i}
                className={`flex items-center gap-1.5 text-[11px] ${todo.complete ? "text-gray-400" : "text-gray-700 font-medium"}`}
              >
                {todo.complete ? (
                  <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                ) : (
                  <span className="h-3 w-3 flex-shrink-0 rounded-full border border-gray-400" />
                )}
                <span className={todo.complete ? "line-through" : ""}>{t(todo.label)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
        <span>
          {vehicle.collectedOn
            ? t("On site: {{date}}", { date: formatDateTime(vehicle.collectedOn, DateTime.DATE_SHORT) })
            : t("Created: {{date}}", { date: formatDateTime(vehicle.created, DateTime.DATE_SHORT) })}
        </span>
        {age !== null && (
          <span className="rounded-full bg-gray-100 border border-gray-300 px-2 py-0.5 font-medium text-gray-700">
            {age}d
          </span>
        )}
      </div>
    </div>
  );
}

export default function VehicleKanbanBoard({
  max = 25,
  fillHeight = false,
}: {
  max?: number;
  // When true, the board stretches to fill its parent's height and each column
  // scrolls independently. When false (e.g. inside a modal) columns cap at 65vh.
  fillHeight?: boolean;
}) {
  const { t } = useTranslation("vehicles");
  const { kanban } = useVehicleKanban(max);
  const { stats } = useVehicleStats();

  // Full pipeline count per stage (the whole funnel, not just the capped cards shown here).
  const fullCounts = useMemo(() => {
    const counts = new Map<VehiclePipelineStage, number>();
    stats.data?.data?.pipelineCounts?.forEach((pc) => counts.set(pc.stage, pc.count));
    return counts;
  }, [stats.data]);

  // Bucket the (already oldest-first) cards into their stage columns.
  const columns = useMemo(() => {
    const grouped = new Map<VehiclePipelineStage, IKanbanVehicle[]>();
    KANBAN_COLUMN_STAGES.forEach((c) => grouped.set(c.stage, []));
    kanban.data?.data?.forEach((item) => {
      grouped.get(item.stage)?.push(item);
    });
    return grouped;
  }, [kanban.data]);

  return (
    <div className={fillHeight ? "flex flex-col h-full min-h-0" : "py-4"}>

      {kanban.isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingWheel />
        </div>
      ) : kanban.isError ? (
        <div className="py-16 text-center text-red-600 text-sm">
          {t("Couldn't load the pipeline board. {{message}}", {
            message: (kanban.error as Error)?.message ?? "",
          })}
        </div>
      ) : (
        <div className={`flex gap-3 overflow-x-auto pb-2${fillHeight ? " flex-1 min-h-0" : ""}`}>
          {KANBAN_COLUMN_STAGES.map((col) => {
            const cards = columns.get(col.stage) ?? [];
            const fullCount = fullCounts.get(col.stage);
            const truncated = fullCount !== undefined && fullCount > cards.length;
            return (
              <div key={col.stage} className={`flex-shrink-0 w-72 bg-gray-50 rounded-lg border border-gray-200${fillHeight ? " flex flex-col min-h-0" : ""}`}>
                <div className="sticky top-0 flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{t(col.labelKey)}</span>
                    {truncated && (
                      <span className="text-[10px] text-gray-400">{t("showing oldest {{count}}", { count: cards.length })}</span>
                    )}
                  </div>
                  <span
                    className="inline-flex items-center justify-center text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-full h-6 min-w-[1.5rem] px-2 leading-none"
                    title={t("Total vehicles in this stage")}
                  >
                    {fullCount ?? cards.length}
                  </span>
                </div>
                <div className={`p-2 flex flex-col gap-2 min-h-[4rem] overflow-y-auto${fillHeight ? " flex-1" : " max-h-[65vh]"}`}>
                  {cards.length === 0 ? (
                    <div className="text-center text-xs text-gray-400 py-4">{t("No vehicles")}</div>
                  ) : (
                    <>
                      {cards.map((c) => <KanbanCard key={c.vehicle.id} item={c} />)}
                      {truncated && (
                        <div className="text-center text-xs font-medium text-gray-500 py-2">
                          {t("+ {{count}} more...", { count: fullCount! - cards.length })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
